import { doorPullDataPlaneIdSchema } from "@zion/sdk-contracts";
//#region src/dp-transport.ts
/**
* The CP-to-DP transport contract of N1 (plan 2026-10-27-cp-dp-network-path, WS-2, decision 3).
*
* Three CP services call the `/internal/*` routes of a data plane: provisioning-svc
* (`DpHttpClient`), site-admin-api (`CpMtlsClient`) and release-engine (`HttpsDpCaCertClient`).
* Each holds one implementation of {@link DpTransportResolver}. The resolver answers one
* {@link DpTransport} per data plane: the public mTLS listener on port 8444, or the tunnel
* terminator. The mode is a per-DP column (`data_placements.transport`, WS-6), so the CP moves the
* fleet one data plane at a time (migration step 5). {@link dpTransportFromRecord} is the one
* decision function, so the three implementations answer the same transport for the same record.
*
* The file also holds the asserted CP service identity (decision 7). The terminator ends the mTLS
* of the CP client, reads the CN of its `cp-trust-ca` leaf, and sends the request over the tunnel
* with {@link CP_SERVICE_IDENTITY_HEADER}. The DP guard reads the header with
* {@link readAssertedCpServiceIdentity}.
*
* Browser-safe: no Node builtin and no Node global. Every function here is pure.
*/
/**
* The two transport modes of a data plane, in migration order.
*
* - `public-mtls`: the CP dials the DP gateway listener `cp-mtls` on port 8444 with its client
*   certificate. This is the path of every data plane until the per-DP switch (migration step 5),
*   and the path closes at S5 (R-007).
* - `tunnel`: the CP dials the terminator door, and the terminator sends the request over the
*   outbound tunnel that the DP agent holds (R-005).
*/
const DP_TRANSPORT_MODES = ["public-mtls", "tunnel"];
/**
* The mode of a data plane that has no explicit mode: `public-mtls`. The WS-6 migration adds the
* column `data_placements.transport` with this default, so every live data plane keeps its path
* until an operator switches it.
*/
const DEFAULT_DP_TRANSPORT_MODE = "public-mtls";
/** True when `value` is exactly one of {@link DP_TRANSPORT_MODES}. */
function isDpTransportMode(value) {
	return typeof value === "string" && DP_TRANSPORT_MODES.includes(value);
}
/**
* The error {@link parseDpTransportMode} throws for a value outside {@link DP_TRANSPORT_MODES}.
*
* `received` is the value as a string: the string itself, cut to its first 64 characters, or
* `typeof value` in angle brackets for a value that is not a string (for example `<number>`).
*/
var UnknownDpTransportModeError = class extends Error {
	name = "UnknownDpTransportModeError";
	received;
	constructor(received) {
		super(`Unknown data plane transport mode ${JSON.stringify(received)}; expected one of ${DP_TRANSPORT_MODES.join(", ")}`);
		this.received = received;
	}
};
/**
* Reads a stored transport mode.
*
* Callers: each resolver, on the `transport` value of a `data_placements` row.
*
* Input: any value. Output: the mode when `value` is exactly one of {@link DP_TRANSPORT_MODES}.
* Errors: throws {@link UnknownDpTransportModeError} for any other value: another string, another
* case (`TUNNEL`), a value with white space, the empty string, `null`, `undefined` or a value that
* is not a string. The defaults row "an unknown transport mode refuses the call" applies: a reader
* never falls back to a mode. Pure.
*/
function parseDpTransportMode(value) {
	if (isDpTransportMode(value)) return value;
	throw new UnknownDpTransportModeError(typeof value === "string" ? value.slice(0, 64) : `<${typeof value}>`);
}
/**
* True when `value` is a data plane id as the tunnel names it: a lower-case UUID, the form of
* `data_placements.id` and of the CN of the per-DP operator certificate. The rule is
* `doorPullDataPlaneIdSchema` of `@zion/sdk-contracts`, which this package already uses for a
* data plane id.
*
* Callers: each resolver, before any read, so a value such as `dp-1`, an upper-case UUID or a path
* fragment never reaches a `uuid` cast or a URL. Pure.
*/
function isDpId(value) {
	return doorPullDataPlaneIdSchema.safeParse(value).success;
}
/** The port of the DP gateway listener `cp-mtls`, which the `public-mtls` mode dials. */
const PUBLIC_MTLS_PORT = 8444;
/** The host of a plain URL: a DNS name or IPv4 text of `A-Z`, `a-z`, `0-9`, `.`, `_`, `-`, or an IPv6 text in `[]`. */
const URL_HOST = String.raw`(?:[A-Za-z0-9._-]+|\[[0-9A-Fa-f:.]+\])`;
/** An optional port of a plain URL: `:` and a decimal from 0 to 65535. A value of 5 digits has no leading zero. */
const URL_PORT = String.raw`(?::(?:[0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5]))?`;
/**
* One character of a path segment (RFC 3986 `pchar` without `@`): an unreserved character, a
* sub-delimiter, `:`, or `%` and two hex digits. So a path holds no `?`, `#`, `@`, `\`, white space,
* control character or character outside ASCII.
*/
const URL_PCHAR = String.raw`(?:[A-Za-z0-9._~!$&'()*+,;=:-]|%[0-9A-Fa-f]{2})`;
/** A path that is empty or ends in a segment of one or more characters, so it ends in no `/`. */
const URL_BASE_PATH = String.raw`(?:(?:/${URL_PCHAR}*)*/${URL_PCHAR}+)?`;
/** The text of a plain `https:` URL, a trailing `/` allowed: the input form of the two builders. */
const PLAIN_HTTPS_URL = new RegExp(String.raw`^https://${URL_HOST}${URL_PORT}(?:/${URL_PCHAR}*)*$`);
/**
* The text of a base URL as `terminatorUrl` carries it: `https://`, a host, an optional port and a
* path that ends in no `/`. It has no user info, query, fragment, white space, control character,
* `\` or character outside ASCII.
*
* `TunnelDpTransportSchema` checks `terminatorUrl` with it, so `z.toJSONSchema` writes it as the
* `pattern` of the field. A JSON Schema validator then refuses an `http:` URL, a trailing `/`, a
* query, a fragment and user info, as the zod schema does. The zod schema also runs
* {@link isHttpsBaseUrl}, which refuses a host that the URL parser refuses (for example
* `999.1.1.1` or `[1:2]`); JSON Schema has no such check.
*
* Exported for `dp-transport.schemas.ts` alone; the package barrel does not export it.
*/
const HTTPS_BASE_URL_PATTERN = new RegExp(String.raw`^https://${URL_HOST}${URL_PORT}${URL_BASE_PATH}$`);
/**
* The text of a base URL as `url` of a `public-mtls` transport carries it: the rule of
* {@link HTTPS_BASE_URL_PATTERN} with the explicit port {@link PUBLIC_MTLS_PORT} (8444).
* `PublicMtlsDpTransportSchema` checks `url` with it, so the JSON Schema of the field refuses
* another port or no port.
*
* Exported for `dp-transport.schemas.ts` alone; the package barrel does not export it.
*/
const PUBLIC_MTLS_BASE_URL_PATTERN = new RegExp(String.raw`^https://${URL_HOST}:${PUBLIC_MTLS_PORT}${URL_BASE_PATH}$`);
/**
* True when the text of `value` matches {@link PLAIN_HTTPS_URL} and the URL parser accepts it. The
* text rule refuses every empty `?`, `#` or `@`, every `\` and all white space, which the parser
* drops or maps and a check of the parsed parts alone accepts. The parser refuses a bad host.
*/
function isPlainHttpsUrl(value) {
	return PLAIN_HTTPS_URL.test(value) && URL.canParse(value);
}
/**
* `value` with every trailing `/` removed. It scans back from the end, so the cost is linear in the
* length of `value`; a regex `/\/+$/` costs the square of the length of a long `/` run. Pure.
*/
function withoutTrailingSlashes(value) {
	let end = value.length;
	while (end > 0 && value.charCodeAt(end - 1) === 47) end -= 1;
	return value.slice(0, end);
}
/**
* True when `value` is a base URL as a transport carries it: it matches
* {@link HTTPS_BASE_URL_PATTERN} (`https://`, a host, an optional port, a path of RFC 3986
* characters with no `@`, and no trailing `/`), and the URL parser accepts it. The rule of `url`
* and `terminatorUrl`; the schemas in `dp-transport.schemas.ts` apply it. Pure.
*/
function isHttpsBaseUrl(value) {
	return HTTPS_BASE_URL_PATTERN.test(value) && URL.canParse(value);
}
/**
* Why a resolver refused a data plane.
*
* - `invalid_dp_id`: the argument is not a lower-case UUID. The resolver read nothing.
* - `dp_not_found`: no `data_placements` row has this id.
* - `no_endpoint`: the resolver holds no address for the mode of the data plane: a `public-mtls`
*   row with no connection string or one that is not a plain `https:` URL, or a `tunnel` row while
*   the resolver holds no plain `https:` terminator URL. A plain `https:` URL has a host the URL
*   parser accepts, no user info, no query and no fragment, and its path holds only RFC 3986
*   characters other than `@`. So its text holds no `?`, `#`, `@`, `\`, white space, control
*   character or character outside ASCII.
* - `unknown_mode`: the stored mode is not one of {@link DP_TRANSPORT_MODES}. A rollback to a
*   build that knows fewer modes gives this answer, and never a fallback to another mode.
*/
const DP_TRANSPORT_RESOLVE_FAILURES = [
	"invalid_dp_id",
	"dp_not_found",
	"no_endpoint",
	"unknown_mode"
];
/**
* The error a resolver rejects with. Each CP service maps `reason` to its own answer; for example
* site-admin-api answers 400 for `invalid_dp_id` and 404 for `dp_not_found`, as its DLQ resolver
* does today. `dpId` is the argument as the caller gave it; the message does not repeat it for
* `invalid_dp_id`, so an untrusted value reaches no log line through the message.
*/
var DpTransportResolveError = class extends Error {
	name = "DpTransportResolveError";
	dpId;
	reason;
	constructor(dpId, reason) {
		super(reason === "invalid_dp_id" ? "Data plane transport refused: the data plane id is not a lower-case UUID" : `Data plane transport refused for ${dpId}: ${reason}`);
		this.dpId = dpId;
		this.reason = reason;
	}
};
/**
* True when `value` is a {@link DpTransportResolveError}, also when a second copy of this package
* made it: the test reads `name` and `reason`, not the prototype. Pure.
*/
function isDpTransportResolveError(value) {
	if (!(value instanceof Error) || value.name !== "DpTransportResolveError") return false;
	const reason = value.reason;
	return typeof reason === "string" && DP_TRANSPORT_RESOLVE_FAILURES.includes(reason);
}
/**
* The `public-mtls` transport of a data plane from its connection string.
*
* Input: `dpId`, a lower-case UUID; `connectionString`, a plain `https:` URL. Output: `url` is the
* connection string with the port set to {@link PUBLIC_MTLS_PORT} and every trailing `/` removed;
* the path stays. `sniHostname` is its host name. For example
* `https://dp-acme.internal.zion.witness.ai/` gives `https://dp-acme.internal.zion.witness.ai:8444`.
*
* Errors: throws {@link DpTransportResolveError} with `invalid_dp_id` for a `dpId` that fails
* {@link isDpId}, and with `no_endpoint` for a `connectionString` that is `null`, empty, not a URL,
* not `https:`, carries user info, a query or a fragment, or holds an empty `?`, `#` or `@`, white
* space, a control character, a `\` or a character outside ASCII. The copies of `toCpMtlsBaseUrl`
* today pass an unparseable value through; this function refuses it. The answer always matches
* {@link PUBLIC_MTLS_BASE_URL_PATTERN}. Pure.
*/
function publicMtlsTransportFor(dpId, connectionString) {
	if (!isDpId(dpId)) throw new DpTransportResolveError(dpId, "invalid_dp_id");
	if (connectionString === null || !isPlainHttpsUrl(connectionString)) throw new DpTransportResolveError(dpId, "no_endpoint");
	const u = new URL(connectionString);
	u.port = String(PUBLIC_MTLS_PORT);
	return {
		mode: "public-mtls",
		dpId,
		url: withoutTrailingSlashes(u.toString()),
		sniHostname: u.hostname
	};
}
/**
* The `tunnel` transport of a data plane.
*
* Input: `dpId`, a lower-case UUID; `terminatorUrl`, a plain `https:` URL, the CP-facing listener
* of the terminator from the resolver's configuration, or `null` when the configuration has none.
* Output: `terminatorUrl` with every trailing `/` removed.
*
* Errors: throws {@link DpTransportResolveError} with `invalid_dp_id` for a `dpId` that fails
* {@link isDpId}, and with `no_endpoint` for a `terminatorUrl` that is `null`, empty, not a URL,
* not `https:`, carries user info, a query or a fragment, or holds an empty `?`, `#` or `@`, white
* space, a control character, a `\` or a character outside ASCII. The answer always matches
* {@link HTTPS_BASE_URL_PATTERN}. Pure.
*/
function tunnelTransportFor(dpId, terminatorUrl) {
	if (!isDpId(dpId)) throw new DpTransportResolveError(dpId, "invalid_dp_id");
	if (terminatorUrl === null || !isPlainHttpsUrl(terminatorUrl)) throw new DpTransportResolveError(dpId, "no_endpoint");
	return {
		mode: "tunnel",
		dpId,
		terminatorUrl: withoutTrailingSlashes(terminatorUrl)
	};
}
/**
* The one decision of every resolver: the transport of `dpId` from its row.
*
* Input: `dpId`; `record`, the row, or `undefined` when no row has the id; `terminatorUrl`, as
* {@link tunnelTransportFor} takes it.
*
* Output per class, checked in this order:
* 1. `dpId` fails {@link isDpId}: throws `invalid_dp_id`.
* 2. `record` is `undefined`: throws `dp_not_found`.
* 3. `record.transport` is not a mode: throws `unknown_mode`.
* 4. mode `public-mtls`: {@link publicMtlsTransportFor} of the connection string.
* 5. mode `tunnel`: {@link tunnelTransportFor}. The connection string plays no part, so a data
*    plane in mode `tunnel` with no connection string still resolves.
*
* Every refusal is a {@link DpTransportResolveError}. Pure.
*/
function dpTransportFromRecord(dpId, record, terminatorUrl) {
	if (!isDpId(dpId)) throw new DpTransportResolveError(dpId, "invalid_dp_id");
	if (record === void 0) throw new DpTransportResolveError(dpId, "dp_not_found");
	if (!isDpTransportMode(record.transport)) throw new DpTransportResolveError(dpId, "unknown_mode");
	return record.transport === "public-mtls" ? publicMtlsTransportFor(dpId, record.connectionString) : tunnelTransportFor(dpId, terminatorUrl);
}
/**
* The request header in which the terminator asserts the CP service that sent a request over the
* tunnel (decision 7). The terminator removes every copy of the header that the CP client sent,
* and sets one copy with the CN of the client's verified `cp-trust-ca` leaf. The DP guard (WS-9)
* trusts the header only on the listener that the tunnel agent reaches, and reads it with
* {@link readAssertedCpServiceIdentity}. Lower case, as Node gives header names.
*/
const CP_SERVICE_IDENTITY_HEADER = "x-zion-cp-service";
/**
* The CP services that call a data plane, one per client class of `research/call-census.md` §1.
* Each value is also the CN of that service's own `cp-trust-ca` leaf (WS-3), so the terminator
* asserts the CN as it reads it. The set retires the fleet CN `cp-platform` (#4560).
*/
const CP_SERVICE_IDENTITIES = [
	"provisioning-svc",
	"release-engine",
	"site-admin-api"
];
/** True when `value` is exactly one of {@link CP_SERVICE_IDENTITIES}. */
function isCpServiceIdentity(value) {
	return CP_SERVICE_IDENTITIES.includes(value);
}
/**
* Reads the asserted CP service identity from the headers of a request on the DP.
*
* Callers: the DP guard of WS-9, after it checked that the request arrived on the tunnel
* listener, never on another listener, where the header is a value the client chose.
*
* Input: the request headers with lower-case names, as Node and NestJS give them; a value is a
* string, an array of strings or `undefined`. Output: see {@link AssertedCpServiceIdentity}. The
* match is exact: no trim and no case fold. An empty array reads as `absent`; an array of one
* value reads as that value. Pure.
*/
function readAssertedCpServiceIdentity(headers) {
	const raw = headers[CP_SERVICE_IDENTITY_HEADER];
	if (raw === void 0) return {
		ok: false,
		reason: "absent"
	};
	let value;
	if (typeof raw === "string") value = raw;
	else {
		const [first] = raw;
		if (first === void 0) return {
			ok: false,
			reason: "absent"
		};
		if (raw.length > 1) return {
			ok: false,
			reason: "repeated"
		};
		value = first;
	}
	if (value.includes(",")) return {
		ok: false,
		reason: "repeated"
	};
	return isCpServiceIdentity(value) ? {
		ok: true,
		identity: value
	} : {
		ok: false,
		reason: "unknown"
	};
}
//#endregion
export { CP_SERVICE_IDENTITIES, CP_SERVICE_IDENTITY_HEADER, DEFAULT_DP_TRANSPORT_MODE, DP_TRANSPORT_MODES, DP_TRANSPORT_RESOLVE_FAILURES, DpTransportResolveError, HTTPS_BASE_URL_PATTERN, PUBLIC_MTLS_BASE_URL_PATTERN, PUBLIC_MTLS_PORT, UnknownDpTransportModeError, dpTransportFromRecord, isDpId, isDpTransportResolveError, isHttpsBaseUrl, parseDpTransportMode, publicMtlsTransportFor, readAssertedCpServiceIdentity, tunnelTransportFor };

//# sourceMappingURL=dp-transport.js.map